import { Menu, MenuItem } from "@mui/material";
import { Check, ChevronDown, CloudUpload, MessageSquare } from "lucide-react";
import { useId, useState } from "react";

export type AttachmentStorage = {
  destination: "chat" | "eqms";
  saved?: boolean;
  saveStarted?: boolean;
  onChange?: (saveToEqms: boolean) => void;
};

export const AttachmentSaveMenu = ({
  title,
  storage,
  disabled,
}: {
  title: string;
  storage: AttachmentStorage;
  disabled: boolean;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const eqms = storage.destination === "eqms";
  const label = eqms ? "Save to eQMS" : "Chat only";
  const Icon = eqms ? CloudUpload : MessageSquare;
  const icon = (
    <Icon
      aria-hidden="true"
      className={`size-[14px] shrink-0 ${eqms ? "text-blue-600" : "text-gray-500"}`}
    />
  );
  if (!storage.onChange || storage.saved || storage.saveStarted) {
    const status = storage.saved
      ? "Saved to eQMS"
      : storage.saveStarted
        ? "Save pending. Send again to retry."
        : label;
    return (
      <span
        aria-label={status}
        title={status}
        className="flex shrink-0 items-center"
      >
        {icon}
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-label={`Storage for ${title}: ${label}`}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-controls={anchor ? menuId : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
        className="flex shrink-0 cursor-pointer items-center gap-1 rounded border-0 bg-transparent p-0 text-gray-500 hover:bg-gray-200 disabled:cursor-default disabled:opacity-50"
      >
        {icon}
        <ChevronDown aria-hidden="true" className="size-3" />
      </button>
      <Menu
        id={menuId}
        container={document.getElementById("root")}
        anchorEl={anchor}
        open={!!anchor && !disabled}
        onClose={() => setAnchor(null)}
        BackdropProps={{
          onMouseDown: (event) => event.stopPropagation(),
        }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClick={(event) => event.stopPropagation()}
        MenuListProps={{ "aria-label": `Storage for ${title}` }}
        PaperProps={{ sx: { maxWidth: 320, borderRadius: 2, mb: 1 } }}
      >
        {[true, false].map((save) => {
          const OptionIcon = save ? CloudUpload : MessageSquare;
          return (
            <MenuItem
              key={String(save)}
              role="menuitemradio"
              aria-checked={eqms === save}
              selected={eqms === save}
              onClick={() => {
                storage.onChange?.(save);
                setAnchor(null);
              }}
              sx={{
                alignItems: "flex-start",
                gap: 1.5,
                whiteSpace: "normal",
                py: 1,
              }}
            >
              <OptionIcon
                aria-hidden="true"
                className={`mt-0.5 size-5 shrink-0 ${save ? "text-blue-600" : "text-gray-500"}`}
              />
              <span className="flex-1">
                <span className="block text-sm">
                  {save ? "Save to eQMS" : "Chat only"}
                </span>
                <span className="block text-xs text-gray-500">
                  {save
                    ? "Will be saved to the QMS on send."
                    : "Use without saving to the QMS"}
                </span>
              </span>
              {eqms === save && (
                <Check
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-blue-600"
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
